/**
 * Hook de estado do onboarding — controla passo atual, persistência no
 * localStorage e integração com pause/resume durante modal interativo.
 *
 * Persistência: localStorage `drpera-ia-onboarding-done:${userId}` = 'true'
 * (chaveado por userId pra "primeira vez por médico" no MESMO browser/dispositivo).
 *
 * TODO backend: salvar onboardingCompleted no UserRecord pra valer entre
 * dispositivos. Documentado em memory/project_backend_pending_web_ia.md.
 */
import { useState, useCallback, useEffect } from 'react';
import { ONBOARDING_STEPS } from '../utils/onboardingSteps';

const LEGACY_STORAGE_KEY = 'drpera-ia-onboarding-done';

/** Gera chave de storage chaveada por userId (fallback pra chave legada se ausente). */
function storageKeyFor(userId: string | null | undefined): string {
  return userId ? `${LEGACY_STORAGE_KEY}:${userId}` : LEGACY_STORAGE_KEY;
}

export interface OnboardingState {
  /** true se onboarding nunca foi completado/pulado (deve ser exibido) */
  shouldShow: boolean;
  /** Índice do step atual (0-based) */
  currentStep: number;
  /** Total de steps */
  totalSteps: number;
  /** Avança para o próximo step */
  goNext: () => void;
  /** Volta para o step anterior */
  goBack: () => void;
  /** Pula o tour sem completar (marca done) */
  skip: () => void;
  /** Completa o tour (marca done) */
  complete: () => void;
  /** true quando o tour está pausado aguardando modal interativo */
  pauseForModal: boolean;
  /** Retoma o tour após fechar modal interativo */
  resumeFromPause: () => void;
  /** Reinicia o tour do zero (botao "?" no Header) — limpa flag done */
  restart: () => void;
}

/**
 * Nome do CustomEvent global que dispara restart do tour.
 * Usado pelo botao "?" no Header sem precisar prop drilling.
 */
export const ONBOARDING_RESTART_EVENT = 'drpera-onboarding-restart';

/**
 * Reseta o onboarding para que apareça novamente.
 * Exportado para uso em futuro botão "Refazer Tour".
 */
export function resetOnboarding(userId?: string | null): void {
  try {
    localStorage.removeItem(storageKeyFor(userId));
    // Limpa também a chave legada por garantia
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // localStorage indisponível — silencioso
  }
}

/** Verifica se o onboarding já foi completado pra esse usuário (ou chave legada) */
function jaCompletou(userId: string | null | undefined): boolean {
  try {
    // Aceita tanto a chave userId-específica quanto a legada (migração suave)
    if (localStorage.getItem(storageKeyFor(userId)) === 'true') return true;
    if (localStorage.getItem(LEGACY_STORAGE_KEY) === 'true') return true;
    return false;
  } catch {
    return false;
  }
}

/** Marca onboarding como concluído no localStorage (chave userId-específica) */
function marcarConcluido(userId: string | null | undefined): void {
  try {
    localStorage.setItem(storageKeyFor(userId), 'true');
  } catch {
    // localStorage indisponível — silencioso
  }
}

export function useOnboardingState(userId?: string | null): OnboardingState {
  const [shouldShow, setShouldShow] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [pauseForModal, setPauseForModal] = useState(false);

  // Verifica ao montar/mudar userId se deve exibir o onboarding
  useEffect(() => {
    setShouldShow(!jaCompletou(userId));
  }, [userId]);

  const goNext = useCallback(() => {
    setPauseForModal(false);
    setCurrentStep((prev) => {
      const next = prev + 1;
      if (next >= ONBOARDING_STEPS.length) {
        // Último step — marca concluído
        marcarConcluido(userId);
        setShouldShow(false);
        return prev;
      }
      return next;
    });
  }, []);

  const goBack = useCallback(() => {
    setPauseForModal(false);
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const skip = useCallback(() => {
    marcarConcluido(userId);
    setShouldShow(false);
    setCurrentStep(0);
    setPauseForModal(false);
  }, []);

  const complete = useCallback(() => {
    marcarConcluido(userId);
    setShouldShow(false);
    setCurrentStep(0);
    setPauseForModal(false);
  }, []);

  const resumeFromPause = useCallback(() => {
    setPauseForModal(false);
    setCurrentStep((prev) => {
      const next = prev + 1;
      if (next >= ONBOARDING_STEPS.length) {
        marcarConcluido(userId);
        setShouldShow(false);
        return prev;
      }
      return next;
    });
  }, []);

  // Quando step com action='open-profile-modal' é ativado, pausa automaticamente
  useEffect(() => {
    const step = ONBOARDING_STEPS[currentStep];
    if (step?.action === 'open-profile-modal' && shouldShow) {
      setPauseForModal(true);
    }
  }, [currentStep, shouldShow]);

  // Reinicia o tour do zero — limpa flag done + reseta state interno.
  // Usado pelo botao "?" no Header (via CustomEvent global, sem prop drilling).
  const restart = useCallback(() => {
    resetOnboarding(userId);
    setCurrentStep(0);
    setPauseForModal(false);
    setShouldShow(true);
  }, [userId]);

  // Escuta CustomEvent global pra restart — botao "?" no Header dispara.
  useEffect(() => {
    const handler = () => restart();
    window.addEventListener(ONBOARDING_RESTART_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_RESTART_EVENT, handler);
  }, [restart]);

  return {
    shouldShow,
    currentStep,
    totalSteps: ONBOARDING_STEPS.length,
    goNext,
    goBack,
    skip,
    complete,
    pauseForModal,
    resumeFromPause,
    restart,
  };
}
